import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import { t } from 'src/lib/language';
import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import {
  DELIVERY_TYPE,
  DownloadOptionsModel, EXTENSION,
  FORMAT,
  ORIENTATION,
} from 'src/models/DownloadModel';
import { FORM } from 'src/constants/Form';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import { makeStyles } from '@material-ui/core/styles';
import { DIALOGS } from 'src/models/DialogModel';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import { setError } from 'src/redux/actions/errorsActions';
import { useState } from 'react';
import { closeDialog } from 'src/redux/actions/dialogActions';
import {
  postDownloadEntitesAction,
  postDownloadSelectedEntitesAction,
  postEmailEntitesAction,
  postEmailSelectedEntitesAction,
} from 'src/redux/actions/querybuilderActions';
import { PAYMENT_STATUS, PaymentModel } from 'src/models/PaymentModel';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import FormCheckbox from 'src/components/Control/FormControls/FormCheckbox';

const useStyles = makeStyles(() => ({
  row: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
  },
}));

enum PaymentType {
  all = 'all',
  selected = 'selected',
  result = 'result',
}

enum PaymentRender {
  payment = 'payment',
  tax = 'tax',
}

const DownloadPaymentDialog = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const lang = useSelector((state: Store) => state.language.language ?? 'en');

  const opened =
    useSelector((state: Store) => state.dialog[DIALOGS.paymentDownload]?.opened || false);
  const getOnlySelected =
    useSelector((state: Store) => state.dialog[DIALOGS.paymentDownload]?.payload || false);

  const count =
    useSelector(
      (state: Store) => (state.querybuilder[ENTITIES.payments]
        && (state.querybuilder[ENTITIES.payments].result?.count || 0)),
    );

  const selections = useSelector(
    (state: Store) => state.selected[ENTITIES.payments] || [],
  );

  const paymentsStatus = useSelector(
    (state: Store) => selections.map(
      (id: number) => {
        if (!state.payments[id]) return undefined;
        return state.payments[id].payment_status;
      },
    )
    || [] as PaymentModel[],
  );

  const [options, setOptions] = useState({
    file: 'payment_receipt.pdf',
    orientation: ORIENTATION.portrait,
    format: FORMAT.pdf,
    delivery: DELIVERY_TYPE.download,
    payment_receipt: true,
  } as DownloadOptionsModel);

  const [type, setType] = useState(PaymentType.selected as PaymentType);
  const [render, setRender] = useState(PaymentRender.tax as PaymentRender);
  const extension = options.format === FORMAT.csv ? EXTENSION.csv : EXTENSION.pdf;

  const handleCancel = () => {
    dispatch(closeDialog({ dialog: DIALOGS.paymentDownload }));
  };

  const validPaymentStatuses = [
    PAYMENT_STATUS.paid,
    PAYMENT_STATUS.cancelled,
    PAYMENT_STATUS.refunded,
  ];

  const handleDownload = () => {
    dispatch(setError({ [FORM.download]: {} }));

    const isPaymentTypeValid = paymentsStatus.every(
      (status: PAYMENT_STATUS) => validPaymentStatuses.includes(status),
    );

    if (!isPaymentTypeValid && type === PaymentType.selected && !options.export_to_grid) {
      dispatch(setAlert({
        type: ALERT_TYPE.warning,
        code: t(lang, 'forms.payments.required_paid_payment'),
      }));
      return;
    }
    if (!options.file) {
      options.file = 'payments';
    }
    options.file = `${options.file.split('.')[0]}.${extension}`;
    if (!options.export_to_grid) {
      options.file = `${options.file.split('.')[0]}.${EXTENSION.pdf}`;
    }
    options.payment_receipt = render === PaymentRender.payment;
    const entity = options.export_to_grid ? ENTITIES.payments : ENTITIES.taxReceipts;
    const customFilterField = options.export_to_grid ? undefined : 'payment_id';

    if (type === PaymentType.all) {
      options.all = true;
      if (options.delivery === DELIVERY_TYPE.download) {
        dispatch(postDownloadEntitesAction(entity, options));
      } else {
        dispatch(postEmailEntitesAction(entity, options));
      }
    }
    if (type === PaymentType.result) {
      if (options.delivery === DELIVERY_TYPE.download) {
        dispatch(postDownloadEntitesAction(entity, options));
      } else {
        dispatch(postEmailEntitesAction(entity, options));
      }
    }
    if (type === PaymentType.selected) {
      options.all = true;
      if (options.delivery === DELIVERY_TYPE.download) {
        dispatch(postDownloadSelectedEntitesAction(
          entity,
          selections,
          options,
          customFilterField,
        ));
      } else {
        dispatch(postEmailSelectedEntitesAction(
          entity,
          selections,
          options,
          customFilterField,
        ));
      }
    }
    dispatch(closeDialog({ dialog: DIALOGS.paymentDownload }));
  };

  const onFieldChange = (value: Value, field: string) => {
    setOptions({
      ...options,
      [field]: value,
    });
  };

  const canDownload = () => {
    if (type === PaymentType.selected) {
      return selections.length < 1000;
    }
    if (type === PaymentType.result) {
      return count < 1000;
    }
    return false;
  };

  return (
    <Dialog
      aria-labelledby="download-receipts"
      open={opened}>
      <DialogTitle id="download-receipts">Get Receipts</DialogTitle>
      <DialogContent dividers>
        <div style={{ width: '480px' }}>
          { !getOnlySelected && (
            <FormControl component="fieldset" style={{ marginTop: '8px' }}>
              <FormLabel component="legend">Payment selection</FormLabel>
              <RadioGroup
                row
                aria-label="payments"
                name="payments"
                value={type}
                onChange={(e) => setType(e.target.value as PaymentType)}>
                <FormControlLabel
                  value={PaymentType.all}
                  control={<Radio />}
                  label="All" />
                <FormControlLabel
                  value={PaymentType.result}
                  control={<Radio />}
                  label={`Result (${count})`} />
                <FormControlLabel
                  disabled={!selections.length}
                  value={PaymentType.selected}
                  control={<Radio />}
                  label={`Selected (${selections.length})`} />
              </RadioGroup>
            </FormControl>
          )}
          <div className={classes.row}>
            <FormTextField
              form={FORM.download}
              label="File Name"
              name="file"
              onChange={onFieldChange}
              required
              value={options.file.split('.')[0]} />
            { `.${'pdf'}` }
          </div>
          <div style={{ marginTop: '8px' }}>
            <FormCheckbox
              checked={options.export_to_grid}
              label="Export"
              name="export_to_grid"
              onChange={(checked:boolean) => onFieldChange(checked, 'export_to_grid')} />
          </div>
          <div />
          { !options.export_to_grid && (
            <>
              <FormControl component="fieldset" style={{ marginTop: '8px' }}>
                <FormLabel component="legend">Receipts</FormLabel>
                <RadioGroup
                  row
                  aria-label="render"
                  name="render"
                  value={render}
                  onChange={(e) => setRender(e.target.value as PaymentRender)}>
                  <FormControlLabel
                    value={PaymentRender.payment}
                    control={<Radio />}
                    label={PaymentRender.payment} />
                  <FormControlLabel
                    value={PaymentRender.tax}
                    control={<Radio />}
                    label={PaymentRender.tax} />
                </RadioGroup>
              </FormControl>
              <div />
              <div />
              <FormControl
                component="fieldset"
                style={{ marginTop: '8px' }}>
                <FormLabel component="legend">Delivery Type</FormLabel>
                <RadioGroup
                  row
                  aria-label="Delivery Type"
                  name="download"
                  value={options.delivery || DELIVERY_TYPE.email}
                  onChange={(e) => onFieldChange(e.target.value, 'delivery')}>
                  <FormControlLabel
                    value={DELIVERY_TYPE.email}
                    control={<Radio />}
                    label="Email Admin" />
                  <FormControlLabel
                    value={DELIVERY_TYPE.contact}
                    control={<Radio />}
                    label="Email Contact" />
                  <FormControlLabel
                    value={DELIVERY_TYPE.download}
                    control={<Radio />}
                    label="Download"
                    disabled={!canDownload()} />
                </RadioGroup>
              </FormControl>
            </>
          )}
          { options.export_to_grid && (
            <>
              <FormControl component="fieldset" style={{ marginTop: '8px' }}>
                <FormLabel component="legend">Format</FormLabel>
                <RadioGroup
                  row
                  aria-label="format"
                  name="format"
                  value={options.format || FORMAT.pdf}
                  onChange={(e) => onFieldChange(e.target.value, 'format')}>
                  <FormControlLabel value={FORMAT.pdf} control={<Radio />} label={FORMAT.pdf} />
                  <FormControlLabel value={FORMAT.csv} control={<Radio />} label={FORMAT.csv} />
                </RadioGroup>
              </FormControl>
              <div />
              <FormControl
                component="fieldset"
                style={{ marginTop: '8px' }}>
                <FormLabel component="legend">Orientation</FormLabel>
                <RadioGroup
                  row
                  aria-label="orientation"
                  name="orientation"
                  value={options.orientation || ORIENTATION.portrait}
                  onChange={(e) => onFieldChange(e.target.value, 'orientation')}>
                  <FormControlLabel
                    value={ORIENTATION.portrait}
                    control={<Radio />}
                    label={ORIENTATION.portrait} />
                  <FormControlLabel
                    value={ORIENTATION.landscape}
                    control={<Radio />}
                    label={ORIENTATION.landscape} />
                </RadioGroup>
              </FormControl>
              <div />
              <FormControl
                component="fieldset"
                style={{ marginTop: '8px' }}>
                <FormLabel component="legend">Delivery Type</FormLabel>
                <RadioGroup
                  row
                  aria-label="Delivery Type"
                  name="download"
                  value={options.delivery || DELIVERY_TYPE.email}
                  onChange={(e) => onFieldChange(e.target.value, 'delivery')}>
                  <FormControlLabel
                    value={DELIVERY_TYPE.email}
                    control={<Radio />}
                    label="By Email" />
                  <FormControlLabel
                    value={DELIVERY_TYPE.download}
                    control={<Radio />}
                    label="Download"
                    disabled={!canDownload()} />
                </RadioGroup>
              </FormControl>
            </>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleCancel} color="primary">
          { t(lang, 'misc.cancel') }
        </Button>
        <Button onClick={handleDownload} color="primary">
          { t(lang, 'misc.get') }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DownloadPaymentDialog;
